import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ContentRecommendations = () => {
  const recommendedContent = [
  {
    id: 1,
    type: "Product Guide",
    title: "Complete Mava Product Catalog 2024",
    description: "Explore our full range of premium bottles with detailed specifications, features, and pricing information.",
    image: "https://images.unsplash.com/photo-1511076953425-88af0f56734c",
    alt: "Modern stainless steel water bottles arranged on white surface with natural lighting",
    downloadUrl: "#",
    icon: "Download",
    size: "2.4 MB PDF",
    color: "from-primary to-accent"
  },
  {
    id: 2,
    type: "Sustainability Report",
    title: "Mava Environmental Impact Report",
    description: "Learn about our commitment to sustainability and the positive environmental impact of choosing Mava.",
    image: "https://images.unsplash.com/photo-1702755528030-4a65c435f4d2",
    alt: "Green leaves with water droplets representing environmental sustainability and nature conservation",
    downloadUrl: "#",
    icon: "Leaf",
    size: "1.8 MB PDF",
    color: "from-success to-accent"
  },
  {
    id: 3,
    type: "Health Guide",
    title: "Optimal Hydration Science Guide",
    description: "Evidence-based insights on hydration benefits, daily water intake recommendations, and health optimization tips.",
    image: "https://images.unsplash.com/photo-1723812792798-dfcc0cbf539f",
    alt: "Open book with scientific diagrams and charts about health and hydration on wooden desk",
    downloadUrl: "#",
    icon: "Heart",
    size: "3.1 MB PDF",
    color: "from-secondary to-primary"
  }];


  const relatedPages = [
  {
    title: "Explore Our Products",
    description: "Discover the perfect bottle for your lifestyle",
    path: "/product-collection",
    icon: "Package",
    color: "bg-primary"
  },
  {
    title: "Our Story",
    description: "Learn about Mava\'s journey and mission",
    path: "/about-page",
    icon: "Heart",
    color: "bg-secondary"
  },
  {
    title: "Contact Us",
    description: "Get in touch for immediate assistance",
    path: "/contact",
    icon: "MessageCircle",
    color: "bg-accent"
  }];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOutBack"
      }
    }
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}>

          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-primary mb-4">
            Explore More About Mava
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            While you wait for our response, discover more about our products, sustainability mission, and hydration science
          </p>
        </motion.div>

        {/* Download Resources */}
        <motion.div
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}>

          <h3 className="text-2xl font-poppins font-semibold text-primary mb-8 text-center">
            Free Resources & Downloads
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {recommendedContent?.map((content) =>
            <motion.div
              key={content?.id}
              className="group cursor-pointer"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}>

                <div className="bg-card border border-border rounded-xl overflow-hidden liquid-shadow hover:shadow-lg brand-transition h-full">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <Image
                    src={content?.image}
                    alt={content?.alt}
                    className="w-full h-full object-cover group-hover:scale-105 brand-transition" />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    
                    {/* Type Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-card/90 backdrop-blur-sm text-primary px-3 py-1 rounded-full text-sm font-medium">
                        {content?.type}
                      </span>
                    </div>

                    {/* Download Icon */}
                    <div className={`absolute bottom-4 right-4 w-10 h-10 bg-gradient-to-br ${content?.color} rounded-full flex items-center justify-center group-hover:scale-110 brand-transition`}>
                      <Icon name={content?.icon} size={20} color="white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h4 className="text-lg font-poppins font-semibold text-primary mb-2 group-hover:text-accent brand-transition">
                      {content?.title}
                    </h4>
                    
                    <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                      {content?.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-text-secondary bg-muted px-2 py-1 rounded">
                        {content?.size}
                      </span>
                      
                      <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      className="water-ripple">

                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Related Pages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}>

          <h3 className="text-2xl font-poppins font-semibold text-primary mb-8 text-center">
            Continue Exploring
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPages?.map((page, index) =>
            <motion.div
              key={page?.path}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}>

                <Link to={page?.path} className="block group">
                  <div className="bg-card border border-border rounded-lg p-6 text-center liquid-shadow hover:shadow-lg brand-transition h-full">
                    <div className={`w-12 h-12 ${page?.color} rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 brand-transition`}>
                      <Icon name={page?.icon} size={24} color="white" />
                    </div>
                    
                    <h4 className="text-lg font-poppins font-semibold text-primary mb-2 group-hover:text-accent brand-transition">
                      {page?.title}
                    </h4>
                    
                    <p className="text-text-secondary text-sm">
                      {page?.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Return to Homepage CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}>

          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-poppins font-semibold text-primary mb-4">
              Ready to Explore More?
            </h3>
            <p className="text-text-secondary mb-6">
              Return to our homepage to discover the complete Mava experience
            </p>
            <Link to="/homepage">
              <Button
                variant="default"
                size="lg"
                iconName="Home"
                iconPosition="left"
                className="bg-action hover:bg-action/90 water-ripple">

                Back to Homepage
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>);

};

export default ContentRecommendations;